import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { RankingQ } from "../../types/quiz";

export type RankingProps = {
  q: RankingQ;
  order: number[];
  onChange: (next: number[]) => void;
};

function SortableItem({
                        id,
                        label,
                        index,
                        onMoveUp,
                        onMoveDown,
                        canMoveUp,
                        canMoveDown,
                      }: {
  id: string;
  label: string;
  index: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  // Prevent drag activation when interacting with the arrow buttons
  const stopDragStart = (
      e: any
  ) => {
    e.stopPropagation();
  };

  return (
      <div
          ref={setNodeRef}
          style={style}
          // Make the entire row draggable
          {...(attributes as any)}
          {...(listeners as any)}
          className={`cq-flex cq-items-center cq-gap-3 cq-border-2 !cq-border-solid cq-border-cfa-blue-500 cq-p-3 cq-py-1 md:cq-p-4 cq-w-full cq-max-w-ranking cq-bg-cfa-bright-blue-30 cq-mx-auto cq-cursor-grab ${
              isDragging ? "!cq-border-black !cq-bg-black cq-text-white" : ""
          }`}
      >
      <span className="cq-inline-flex cq-h-8 cq-w-8 md:cq-h-10 md:cq-w-10 cq-text-xl md:cq-text-2xl cq-items-center cq-justify-center cq-bg-cfa-blue-500 cq-text-black cq-font-medium cq-font-azo">
        {index + 1}
      </span>


        <button
            type="button"
            className={`handle cq-cursor-grab cq-hidden md:cq-flex cq-items-center cq-justify-center ${isDragging ? 'cq-text-white' : ''}`}
            aria-label="Drag to reorder"
        >
          <svg
              className="cq-w-[30px] cq-h-[30px]"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7 8H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M7 16H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <span className="cq-flex-1 cq-text-sm md:cq-text-lg cq-text-balance">
        {label}
      </span>

        {/* Up/Down arrows — remain clickable; block drag start */}
        <span className="cq-flex cq-flex-col cq-items-center cq-gap-0 sm:cq-gap-1 cq-select-none">
        <button
            type="button"
            onPointerDown={stopDragStart}
            onMouseDown={stopDragStart}
            onTouchStart={stopDragStart}
            onClick={onMoveUp}
            disabled={!canMoveUp}
            aria-label="Move up"
            className={`cq-p-1 cq-leading-[0] ${isDragging ? 'cq-text-white': ''} ${
                !canMoveUp ? "cq-opacity-40 cq-cursor-not-allowed" : "cq-cursor-pointer"
            }`}
        >
          <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="cq-shrink-0"
          >
            <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path
                d="M7 10L12 5L17 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
            type="button"
            onPointerDown={stopDragStart}
            onMouseDown={stopDragStart}
            onTouchStart={stopDragStart}
            onClick={onMoveDown}
            disabled={!canMoveDown}
            aria-label="Move down"
            className={`cq-p-1 cq-leading-[0] ${isDragging ? 'cq-text-white': ''} ${
                !canMoveDown ? "cq-opacity-40 cq-cursor-not-allowed" : "cq-cursor-pointer"
            }`}
        >
          <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="cq-shrink-0"
          >
            <path d="M12 19L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path
                d="M7 14L12 19L17 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
          </svg>
        </button>
      </span>
      </div>
  );
}

export function Ranking({ q, order, onChange }: RankingProps) {
  const items = order.map((idx) => ({
    id: `${q.id}-${idx}`,
    label: q.options[idx].text,
    optIndex: idx,
  }));

  const sensors = useSensors(
      useSensor(PointerSensor, {activationConstraint: {distance: 5}})
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    onChange(arrayMove(order, oldIndex, newIndex));
  }

  function move(index: number, delta: number) {
    const next = index + delta;
    if (next < 0 || next >= order.length) return;
    onChange(arrayMove(order, index, next));
  }

  return (
      <div className="cq-touch-none">
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="cq-space-y-3 cq-max-w-content cq-mx-auto cq-pl-0">
              {items.map((item, idx) => (
                  <li key={item.id} className="cq-list-none cq-pl-0">
                    <SortableItem
                        id={item.id}
                        label={item.label}
                        index={idx}
                        onMoveUp={() => move(idx, -1)}
                        onMoveDown={() => move(idx, 1)}
                        canMoveUp={idx > 0}
                        canMoveDown={idx < items.length - 1}
                    />
                  </li>
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </div>
  );
}

export default Ranking;
